(() => {
  const initializedPreviews = new WeakSet();
  const colorKeys = ["black", "beige", "red", "blue", "pink", "orange", "aqua", "grey"];
  const desktop = window.matchMedia("(min-width: 990px)");

  const interpolate = (template, name, phone) =>
    String(template || "")
      .split("[name]")
      .join(name)
      .split("[phone]")
      .join(phone);

  const normalizeColor = (value) => {
    const color = String(value || "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .trim()
      .toLowerCase();
    const includesAny = (...aliases) => aliases.some((alias) => color.includes(alias));
    if (color.includes("beige")) return "beige";
    if (includesAny("red", "rot", "rouge", "rojo", "rosso", "rood")) return "red";
    if (includesAny("sky blue", "blue", "blau", "bleu", "azul", "azzurro", "blu", "blauw")) {
      return "blue";
    }
    if (includesAny("pink", "rosa", "rose", "roze")) return "pink";
    if (includesAny("orange", "naranja", "arancione", "oranje")) return "orange";
    if (includesAny("aqua", "acqua", "aguamarina", "acquamarina")) return "aqua";
    if (includesAny("grey", "gray", "grau", "gris", "grigio", "grijs")) return "grey";
    return "black";
  };

  const fitLine = (output) => {
    output.style.removeProperty("font-size");
    if (!output.textContent || output.offsetParent === null) return;

    const availableWidth = output.clientWidth;
    const requiredWidth = output.scrollWidth;
    if (!availableWidth || requiredWidth <= availableWidth) return;

    const startingSize = Number.parseFloat(window.getComputedStyle(output).fontSize);
    if (!Number.isFinite(startingSize)) return;

    const fittedSize = Math.max(6, startingSize * (availableWidth / requiredWidth) * 0.98);
    output.style.fontSize = fittedSize.toFixed(2) + "px";
  };

  const createGalleryProjection = (preview, mediaId) => {
    const sourceVisual = preview.querySelector("[data-tag-preview-visual]");
    const sourceImage = preview.querySelector("[data-tag-preview-image]");
    if (!sourceVisual || !sourceImage) return null;

    const frame = document.createElement("div");
    frame.id = mediaId;
    frame.className = "shop-main-image fourpaw-personalization-gallery-frame";
    frame.setAttribute("role", "group");
    frame.setAttribute("aria-label", preview.dataset.galleryThumbnailLabel || "Personalized tag preview");
    frame.setAttribute("data-clarity-mask", "true");

    const stage = document.createElement("div");
    stage.className = "fourpaw-personalization-gallery-stage";

    const visual = sourceVisual.cloneNode(true);
    visual.classList.add("fourpaw-personalization-gallery-visual");
    visual.setAttribute("data-clarity-mask", "true");
    stage.append(visual);
    frame.append(stage);

    const label = document.createElement("span");
    label.className = "fourpaw-personalization-gallery-label";
    label.textContent = preview.dataset.galleryThumbnailLabel || "Personalized tag preview";
    frame.append(label);

    const thumbnail = document.createElement("button");
    thumbnail.type = "button";
    thumbnail.className = "shop-thumbnail page-fade-in fourpaw-personalization-gallery-thumb";
    thumbnail.setAttribute("aria-label", preview.dataset.galleryThumbnailLabel || "Personalized tag preview");
    thumbnail.setAttribute("aria-controls", mediaId);
    thumbnail.setAttribute("aria-current", "false");

    const square = document.createElement("span");
    square.className = "thumb-square";
    const thumbnailImage = document.createElement("img");
    thumbnailImage.src = sourceImage.currentSrc || sourceImage.src;
    thumbnailImage.alt = "";
    thumbnailImage.loading = "lazy";
    thumbnailImage.decoding = "async";
    thumbnailImage.width = 144;
    thumbnailImage.height = 144;
    square.append(thumbnailImage);
    thumbnail.append(square);

    return {
      frame,
      thumbnail,
      thumbnailImage,
      image: visual.querySelector("[data-tag-preview-image]"),
      nameOutput: visual.querySelector("[data-tag-preview-name]"),
      phoneOutput: visual.querySelector("[data-tag-preview-phone]"),
      visual,
    };
  };

  const initializePreview = (preview) => {
    if (initializedPreviews.has(preview)) return;

    const scope = preview.closest(".shop-product-section") || document;
    const galleryElement = scope.querySelector(".product-gallery");
    const nameInput = scope.querySelector('[data-fourpaw-personalization-role="dog_name"] input');
    const phoneInput = scope.querySelector('[data-fourpaw-personalization-role="phone"] input');
    const sourceTarget = {
      nameOutput: preview.querySelector("[data-tag-preview-name]"),
      phoneOutput: preview.querySelector("[data-tag-preview-phone]"),
      visual: preview.querySelector("[data-tag-preview-visual]"),
      image: preview.querySelector("[data-tag-preview-image]"),
    };
    if (!sourceTarget.nameOutput || !sourceTarget.phoneOutput || !sourceTarget.visual || !sourceTarget.image) return;

    const mediaId = "fourpaw-personalization-gallery-" + (preview.id || "preview");
    const namePlaceholder = preview.dataset.namePlaceholder || "";
    const phonePlaceholder = preview.dataset.phonePlaceholder || "";
    const labelTemplates = {
      empty: preview.dataset.accessibilityLabelEmptyTemplate || "",
      nameOnly: preview.dataset.accessibilityLabelNameTemplate || "[name]",
      phoneOnly: preview.dataset.accessibilityLabelPhoneTemplate || "[phone]",
      both: preview.dataset.accessibilityLabelTemplate || "[name] [phone]",
    };
    let gallery = null;
    let projection = null;

    const targets = () => (projection ? [sourceTarget, projection] : [sourceTarget]);

    const syncColor = () => {
      const selectedColor = scope.querySelector(".simple-variant-picker__option.option--color input:checked");
      const stableColorKey = selectedColor?.dataset.fourpawColorKey || "";
      const colorKey = colorKeys.includes(stableColorKey) ? stableColorKey : normalizeColor(selectedColor?.value);
      const datasetKey = "previewImage" + colorKey[0].toUpperCase() + colorKey.slice(1);
      const source = preview.dataset[datasetKey] || preview.dataset.previewImageBlack || "";
      targets().forEach((target) => {
        if (source && target.image?.getAttribute("src") !== source) target.image.setAttribute("src", source);
      });
      if (source && projection && projection.thumbnailImage.getAttribute("src") !== source) {
        projection.thumbnailImage.setAttribute("src", source);
      }
      preview.dataset.previewColor = colorKeys.includes(colorKey) ? colorKey : "black";
    };

    const render = () => {
      const nameValue = String(nameInput?.value || "").trim();
      const phoneValue = String(phoneInput?.value || "").trim();
      const hasName = Boolean(nameValue);
      const hasPhone = Boolean(phoneValue);
      const layout = hasName && hasPhone ? "both" : hasName ? "name-only" : hasPhone ? "phone-only" : "empty";
      const visibleName = layout === "empty" ? namePlaceholder : nameValue;
      const visiblePhone = layout === "empty" ? phonePlaceholder : phoneValue;
      const labelTemplate =
        layout === "name-only"
          ? labelTemplates.nameOnly
          : layout === "phone-only"
            ? labelTemplates.phoneOnly
            : labelTemplates[layout];

      preview.dataset.layout = layout;
      if (projection) projection.frame.dataset.layout = layout;
      targets().forEach((target) => {
        target.nameOutput.textContent = visibleName;
        target.phoneOutput.textContent = visiblePhone;
        target.visual.setAttribute("aria-label", interpolate(labelTemplate, nameValue, phoneValue));
      });
      window.requestAnimationFrame(() => {
        targets().forEach((target) => {
          fitLine(target.nameOutput);
          fitLine(target.phoneOutput);
        });
      });
    };

    const registerProjection = () => {
      if (projection || !galleryElement?.__galleryInstance) return;
      const candidate = createGalleryProjection(preview, mediaId);
      if (!candidate) return;
      gallery = galleryElement.__galleryInstance;
      const registered = gallery.registerVirtualMedia({
        id: mediaId,
        media: candidate.frame,
        thumbnail: candidate.thumbnail,
      });
      if (registered === false) return;
      projection = candidate;
      syncColor();
      render();
    };

    const syncPlacement = () => {
      if (desktop.matches) registerProjection();
      const useGallery = Boolean(desktop.matches && projection && !preview.hidden);
      preview.toggleAttribute("data-desktop-gallery-source", useGallery);
      gallery?.setVirtualMediaHidden(mediaId, !useGallery);
      if (useGallery) gallery?.setMediaById(mediaId);
    };

    const activateGalleryPreview = () => {
      if (!desktop.matches || preview.hidden) return;
      registerProjection();
      gallery?.setMediaById(mediaId);
      window.requestAnimationFrame(render);
    };

    const bindField = (input, role) => {
      if (!input) return;
      input.addEventListener("input", () => {
        render();
        activateGalleryPreview();
      });
      input.addEventListener("change", () => {
        render();
        activateGalleryPreview();
      });
      input.addEventListener("focus", () => {
        preview.dataset.activeField = role;
        if (projection) projection.frame.dataset.activeField = role;
        activateGalleryPreview();
      });
      input.addEventListener("blur", () => {
        if (preview.dataset.activeField === role) preview.removeAttribute("data-active-field");
        if (projection?.frame.dataset.activeField === role) {
          projection.frame.removeAttribute("data-active-field");
        }
      });
    };

    bindField(nameInput, "dog_name");
    bindField(phoneInput, "phone");
    document.addEventListener("simple-variant-picker:variantChange", () => {
      syncColor();
      render();
    });
    document.addEventListener("variant:change", () => {
      syncColor();
      render();
    });
    document.addEventListener("product-gallery:ready", (event) => {
      if (event.target !== galleryElement) return;
      syncPlacement();
    });
    desktop.addEventListener("change", syncPlacement);
    window.addEventListener("resize", render, { passive: true });

    const visibilityObserver = new MutationObserver(() => {
      syncPlacement();
      if (!preview.hidden) render();
    });
    visibilityObserver.observe(preview, { attributes: true, attributeFilter: ["hidden"] });

    syncColor();
    render();
    syncPlacement();
    initializedPreviews.add(preview);
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll("[data-fourpaw-tag-preview]").forEach(initializePreview);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
